// src/components/auth/constants.js
/**
 * Central Constants for EraBangun PMO
 * Single source of truth for roles, permissions, status, and configurations
 */

// ============================================
// USER ROLES (18 Total)
// ============================================

export const USER_ROLES = {
  // Tier 1: Super Admin
  ADMINISTRATOR: 'ADMINISTRATOR',
  
  // Tier 2: Directors (C-Level)
  DIRECTOR: 'DIRECTOR',
  FINANCE_DIRECTOR: 'FINANCE_DIRECTOR',
  
  // Tier 3: Department Managers
  SALES_MANAGER: 'SALES_MANAGER',
  PROCUREMENT_MANAGER: 'PROCUREMENT_MANAGER',
  FINANCE_MANAGER: 'FINANCE_MANAGER',
  PLANNING_ENGINEERING_MANAGER: 'PLANNING_ENGINEERING_MANAGER',
  
  // Tier 4: Operational Staff
  SALES: 'SALES',
  PLANNING_ENGINEERING: 'PLANNING_ENGINEERING',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  PROJECT_CONTROL: 'PROJECT_CONTROL',
  PROJECT_ADMIN: 'PROJECT_ADMIN',
  PROCUREMENT: 'PROCUREMENT',
  LOGISTICS: 'LOGISTICS',
  FINANCE: 'FINANCE',
  BUDGET_CONTROL: 'BUDGET_CONTROL',
  
  // Tier 5: Field Workers
  SUPERVISOR: 'SUPERVISOR',
  MANDOR: 'MANDOR',
};

// Role display names
export const ROLE_LABELS = {
  [USER_ROLES.ADMINISTRATOR]: 'Administrator',
  [USER_ROLES.DIRECTOR]: 'Director',
  [USER_ROLES.FINANCE_DIRECTOR]: 'Finance Director',
  [USER_ROLES.SALES_MANAGER]: 'Sales Manager',
  [USER_ROLES.PROCUREMENT_MANAGER]: 'Procurement Manager',
  [USER_ROLES.FINANCE_MANAGER]: 'Finance Manager',
  [USER_ROLES.PLANNING_ENGINEERING_MANAGER]: 'Planning Engineering Manager',
  [USER_ROLES.SALES]: 'Sales',
  [USER_ROLES.PLANNING_ENGINEERING]: 'Planning Engineering',
  [USER_ROLES.PROJECT_MANAGER]: 'Project Manager',
  [USER_ROLES.PROJECT_CONTROL]: 'Project Control',
  [USER_ROLES.PROJECT_ADMIN]: 'Project Admin',
  [USER_ROLES.PROCUREMENT]: 'Procurement',
  [USER_ROLES.LOGISTICS]: 'Logistics',
  [USER_ROLES.FINANCE]: 'Finance',
  [USER_ROLES.BUDGET_CONTROL]: 'Budget Control',
  [USER_ROLES.SUPERVISOR]: 'Supervisor',
  [USER_ROLES.MANDOR]: 'Mandor',
};

// Role descriptions
export const ROLE_DESCRIPTIONS = {
  [USER_ROLES.ADMINISTRATOR]: 'Full system access with all permissions',
  [USER_ROLES.DIRECTOR]: 'Company director with strategic oversight',
  [USER_ROLES.FINANCE_DIRECTOR]: 'Chief Financial Officer with full financial authority',
  [USER_ROLES.SALES_MANAGER]: 'Sales team management and strategy',
  [USER_ROLES.PROCUREMENT_MANAGER]: 'Procurement team management and vendor strategy',
  [USER_ROLES.FINANCE_MANAGER]: 'Financial management and team oversight',
  [USER_ROLES.PLANNING_ENGINEERING_MANAGER]: 'Planning engineering team management and oversight',
  [USER_ROLES.SALES]: 'Customer relationship and SPK creation',
  [USER_ROLES.PLANNING_ENGINEERING]: 'Technical planning and milestone templates',
  [USER_ROLES.PROJECT_MANAGER]: 'Project oversight and team management',
  [USER_ROLES.PROJECT_CONTROL]: 'Progress monitoring and reporting',
  [USER_ROLES.PROJECT_ADMIN]: 'Administrative support for projects',
  [USER_ROLES.PROCUREMENT]: 'Material and vendor management',
  [USER_ROLES.LOGISTICS]: 'Material distribution and tracking',
  [USER_ROLES.FINANCE]: 'Financial management and payments',
  [USER_ROLES.BUDGET_CONTROL]: 'Budget oversight and approval',
  [USER_ROLES.SUPERVISOR]: 'Field supervision and progress reporting',
  [USER_ROLES.MANDOR]: 'Field worker and progress reporting',
};

// Role tiers for hierarchy
export const ROLE_TIERS = {
  1: [USER_ROLES.ADMINISTRATOR],
  2: [USER_ROLES.DIRECTOR, USER_ROLES.FINANCE_DIRECTOR],
  3: [
    USER_ROLES.SALES_MANAGER,
    USER_ROLES.PROCUREMENT_MANAGER,
    USER_ROLES.FINANCE_MANAGER,
    USER_ROLES.PLANNING_ENGINEERING_MANAGER,
  ],
  4: [
    USER_ROLES.SALES,
    USER_ROLES.PLANNING_ENGINEERING,
    USER_ROLES.PROJECT_MANAGER,
    USER_ROLES.PROJECT_CONTROL,
    USER_ROLES.PROJECT_ADMIN,
    USER_ROLES.PROCUREMENT,
    USER_ROLES.LOGISTICS,
    USER_ROLES.FINANCE,
    USER_ROLES.BUDGET_CONTROL,
  ],
  5: [USER_ROLES.SUPERVISOR, USER_ROLES.MANDOR],
};

// ============================================
// PERMISSIONS (63 Total)
// ============================================

export const PERMISSIONS = {
  // ===== USER MANAGEMENT (4) =====
  USER_CREATE: 'USER_CREATE',
  USER_VIEW: 'USER_VIEW',
  USER_UPDATE: 'USER_UPDATE',
  USER_DELETE: 'USER_DELETE',
  
  // ===== CUSTOMER MANAGEMENT (4) =====
  CUSTOMER_CREATE: 'CUSTOMER_CREATE',
  CUSTOMER_VIEW: 'CUSTOMER_VIEW',
  CUSTOMER_UPDATE: 'CUSTOMER_UPDATE',
  CUSTOMER_DELETE: 'CUSTOMER_DELETE',
  
  // ===== SPK MANAGEMENT (5) =====
  SPK_CREATE: 'SPK_CREATE',
  SPK_VIEW: 'SPK_VIEW',
  SPK_UPDATE: 'SPK_UPDATE',
  SPK_DELETE: 'SPK_DELETE',
  SPK_APPROVE: 'SPK_APPROVE',
  
  // ===== PROJECT MANAGEMENT (4) =====
  PROJECT_CREATE: 'PROJECT_CREATE',
  PROJECT_VIEW: 'PROJECT_VIEW',
  PROJECT_UPDATE: 'PROJECT_UPDATE',
  PROJECT_DELETE: 'PROJECT_DELETE',
  
  // ===== FO SEGMENT MANAGEMENT (4) =====
  FO_SEGMENT_CREATE: 'FO_SEGMENT_CREATE',
  FO_SEGMENT_VIEW: 'FO_SEGMENT_VIEW',
  FO_SEGMENT_UPDATE: 'FO_SEGMENT_UPDATE',
  FO_SEGMENT_DELETE: 'FO_SEGMENT_DELETE',
  
  // ===== TOWER SITE MANAGEMENT (4) =====
  TOWER_SITE_CREATE: 'TOWER_SITE_CREATE',
  TOWER_SITE_VIEW: 'TOWER_SITE_VIEW',
  TOWER_SITE_UPDATE: 'TOWER_SITE_UPDATE',
  TOWER_SITE_DELETE: 'TOWER_SITE_DELETE',
  
  // ===== ITEM MANAGEMENT (4) =====
  ITEM_CREATE: 'ITEM_CREATE',
  ITEM_VIEW: 'ITEM_VIEW',
  ITEM_UPDATE: 'ITEM_UPDATE',
  ITEM_DELETE: 'ITEM_DELETE',
  
  // ===== MAIN AREA MANAGEMENT (4) =====
  MAIN_AREA_CREATE: 'MAIN_AREA_CREATE',
  MAIN_AREA_VIEW: 'MAIN_AREA_VIEW',
  MAIN_AREA_UPDATE: 'MAIN_AREA_UPDATE',
  MAIN_AREA_DELETE: 'MAIN_AREA_DELETE',
  
  // ===== BUDGET MANAGEMENT (5) =====
  BUDGET_CREATE: 'BUDGET_CREATE',
  BUDGET_VIEW: 'BUDGET_VIEW',
  BUDGET_UPDATE: 'BUDGET_UPDATE',
  BUDGET_DELETE: 'BUDGET_DELETE',
  BUDGET_APPROVE: 'BUDGET_APPROVE',
  
  // ===== RAP MANAGEMENT (5) =====
  RAP_CREATE: 'RAP_CREATE',
  RAP_VIEW: 'RAP_VIEW',
  RAP_UPDATE: 'RAP_UPDATE',
  RAP_DELETE: 'RAP_DELETE',
  RAP_APPROVE: 'RAP_APPROVE',
  
  // ===== MILESTONE MANAGEMENT (5) =====
  MILESTONE_CREATE: 'MILESTONE_CREATE',
  MILESTONE_VIEW: 'MILESTONE_VIEW',
  MILESTONE_UPDATE: 'MILESTONE_UPDATE',
  MILESTONE_DELETE: 'MILESTONE_DELETE',
  MILESTONE_APPROVE: 'MILESTONE_APPROVE',
  
  // ===== MILESTONE TEMPLATE MANAGEMENT (4) =====
  MILESTONE_TEMPLATE_CREATE: 'MILESTONE_TEMPLATE_CREATE',
  MILESTONE_TEMPLATE_VIEW: 'MILESTONE_TEMPLATE_VIEW',
  MILESTONE_TEMPLATE_UPDATE: 'MILESTONE_TEMPLATE_UPDATE',
  MILESTONE_TEMPLATE_DELETE: 'MILESTONE_TEMPLATE_DELETE',
  
  // ===== SUPERVISOR ASSIGNMENT (3) =====
  SUPERVISOR_ASSIGN: 'SUPERVISOR_ASSIGN',
  SUPERVISOR_VIEW: 'SUPERVISOR_VIEW',
  SUPERVISOR_REVOKE: 'SUPERVISOR_REVOKE',
  
  // ===== EVIDENCE MANAGEMENT (4) =====
  EVIDENCE_UPLOAD: 'EVIDENCE_UPLOAD',
  EVIDENCE_VIEW: 'EVIDENCE_VIEW',
  EVIDENCE_DELETE: 'EVIDENCE_DELETE',
  EVIDENCE_APPROVE: 'EVIDENCE_APPROVE',
  
  // ===== REPORT MANAGEMENT (2) =====
  REPORT_VIEW: 'REPORT_VIEW',
  REPORT_EXPORT: 'REPORT_EXPORT',
  
  // ===== SYSTEM MANAGEMENT (2) =====
  SYSTEM_SETTINGS: 'SYSTEM_SETTINGS',
  AUDIT_VIEW: 'AUDIT_VIEW',
};

// Permission labels
export const PERMISSION_LABELS = {
  // User Management
  [PERMISSIONS.USER_CREATE]: 'Create User',
  [PERMISSIONS.USER_VIEW]: 'View User',
  [PERMISSIONS.USER_UPDATE]: 'Update User',
  [PERMISSIONS.USER_DELETE]: 'Delete User',
  
  // Customer Management
  [PERMISSIONS.CUSTOMER_CREATE]: 'Create Customer',
  [PERMISSIONS.CUSTOMER_VIEW]: 'View Customer',
  [PERMISSIONS.CUSTOMER_UPDATE]: 'Update Customer',
  [PERMISSIONS.CUSTOMER_DELETE]: 'Delete Customer',
  
  // SPK Management
  [PERMISSIONS.SPK_CREATE]: 'Create SPK',
  [PERMISSIONS.SPK_VIEW]: 'View SPK',
  [PERMISSIONS.SPK_UPDATE]: 'Update SPK',
  [PERMISSIONS.SPK_DELETE]: 'Delete SPK',
  [PERMISSIONS.SPK_APPROVE]: 'Approve SPK',
  
  // Project Management
  [PERMISSIONS.PROJECT_CREATE]: 'Create Project',
  [PERMISSIONS.PROJECT_VIEW]: 'View Project',
  [PERMISSIONS.PROJECT_UPDATE]: 'Update Project',
  [PERMISSIONS.PROJECT_DELETE]: 'Delete Project',
  
  // FO Segment Management
  [PERMISSIONS.FO_SEGMENT_CREATE]: 'Create FO Segment',
  [PERMISSIONS.FO_SEGMENT_VIEW]: 'View FO Segment',
  [PERMISSIONS.FO_SEGMENT_UPDATE]: 'Update FO Segment',
  [PERMISSIONS.FO_SEGMENT_DELETE]: 'Delete FO Segment',
  
  // Tower Site Management
  [PERMISSIONS.TOWER_SITE_CREATE]: 'Create Tower Site',
  [PERMISSIONS.TOWER_SITE_VIEW]: 'View Tower Site',
  [PERMISSIONS.TOWER_SITE_UPDATE]: 'Update Tower Site',
  [PERMISSIONS.TOWER_SITE_DELETE]: 'Delete Tower Site',
  
  // Item Management
  [PERMISSIONS.ITEM_CREATE]: 'Create Item',
  [PERMISSIONS.ITEM_VIEW]: 'View Item',
  [PERMISSIONS.ITEM_UPDATE]: 'Update Item',
  [PERMISSIONS.ITEM_DELETE]: 'Delete Item',
  
  // Main Area Management
  [PERMISSIONS.MAIN_AREA_CREATE]: 'Create Main Area',
  [PERMISSIONS.MAIN_AREA_VIEW]: 'View Main Area',
  [PERMISSIONS.MAIN_AREA_UPDATE]: 'Update Main Area',
  [PERMISSIONS.MAIN_AREA_DELETE]: 'Delete Main Area',
  
  // Budget Management
  [PERMISSIONS.BUDGET_CREATE]: 'Create Budget',
  [PERMISSIONS.BUDGET_VIEW]: 'View Budget',
  [PERMISSIONS.BUDGET_UPDATE]: 'Update Budget',
  [PERMISSIONS.BUDGET_DELETE]: 'Delete Budget',
  [PERMISSIONS.BUDGET_APPROVE]: 'Approve Budget',
  
  // RAP Management
  [PERMISSIONS.RAP_CREATE]: 'Create RAP',
  [PERMISSIONS.RAP_VIEW]: 'View RAP',
  [PERMISSIONS.RAP_UPDATE]: 'Update RAP',
  [PERMISSIONS.RAP_DELETE]: 'Delete RAP',
  [PERMISSIONS.RAP_APPROVE]: 'Approve RAP',
  
  // Milestone Management
  [PERMISSIONS.MILESTONE_CREATE]: 'Create Milestone',
  [PERMISSIONS.MILESTONE_VIEW]: 'View Milestone',
  [PERMISSIONS.MILESTONE_UPDATE]: 'Update Milestone',
  [PERMISSIONS.MILESTONE_DELETE]: 'Delete Milestone',
  [PERMISSIONS.MILESTONE_APPROVE]: 'Approve Milestone',
  
  // Milestone Template Management
  [PERMISSIONS.MILESTONE_TEMPLATE_CREATE]: 'Create Milestone Template',
  [PERMISSIONS.MILESTONE_TEMPLATE_VIEW]: 'View Milestone Template',
  [PERMISSIONS.MILESTONE_TEMPLATE_UPDATE]: 'Update Milestone Template',
  [PERMISSIONS.MILESTONE_TEMPLATE_DELETE]: 'Delete Milestone Template',
  
  // Supervisor Assignment
  [PERMISSIONS.SUPERVISOR_ASSIGN]: 'Assign Supervisor',
  [PERMISSIONS.SUPERVISOR_VIEW]: 'View Supervisor Assignment',
  [PERMISSIONS.SUPERVISOR_REVOKE]: 'Revoke Supervisor',
  
  // Evidence Management
  [PERMISSIONS.EVIDENCE_UPLOAD]: 'Upload Evidence',
  [PERMISSIONS.EVIDENCE_VIEW]: 'View Evidence',
  [PERMISSIONS.EVIDENCE_DELETE]: 'Delete Evidence',
  [PERMISSIONS.EVIDENCE_APPROVE]: 'Approve Evidence',
  
  // Report Management
  [PERMISSIONS.REPORT_VIEW]: 'View Reports',
  [PERMISSIONS.REPORT_EXPORT]: 'Export Reports',
  
  // System Management
  [PERMISSIONS.SYSTEM_SETTINGS]: 'System Settings',
  [PERMISSIONS.AUDIT_VIEW]: 'View Audit Logs',
};

// Permission categories for UI grouping
export const PERMISSION_CATEGORIES = {
  'User Management': [
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_VIEW,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
  ],
  'Customer Management': [
    PERMISSIONS.CUSTOMER_CREATE,
    PERMISSIONS.CUSTOMER_VIEW,
    PERMISSIONS.CUSTOMER_UPDATE,
    PERMISSIONS.CUSTOMER_DELETE,
  ],
  'SPK Management': [
    PERMISSIONS.SPK_CREATE,
    PERMISSIONS.SPK_VIEW,
    PERMISSIONS.SPK_UPDATE,
    PERMISSIONS.SPK_DELETE,
    PERMISSIONS.SPK_APPROVE,
  ],
  'Project Management': [
    PERMISSIONS.PROJECT_CREATE,
    PERMISSIONS.PROJECT_VIEW,
    PERMISSIONS.PROJECT_UPDATE,
    PERMISSIONS.PROJECT_DELETE,
  ],
  'FO Segment Management': [
    PERMISSIONS.FO_SEGMENT_CREATE,
    PERMISSIONS.FO_SEGMENT_VIEW,
    PERMISSIONS.FO_SEGMENT_UPDATE,
    PERMISSIONS.FO_SEGMENT_DELETE,
  ],
  'Tower Site Management': [
    PERMISSIONS.TOWER_SITE_CREATE,
    PERMISSIONS.TOWER_SITE_VIEW,
    PERMISSIONS.TOWER_SITE_UPDATE,
    PERMISSIONS.TOWER_SITE_DELETE,
  ],
  'Item Management': [
    PERMISSIONS.ITEM_CREATE,
    PERMISSIONS.ITEM_VIEW,
    PERMISSIONS.ITEM_UPDATE,
    PERMISSIONS.ITEM_DELETE,
  ],
  'Main Area Management': [
    PERMISSIONS.MAIN_AREA_CREATE,
    PERMISSIONS.MAIN_AREA_VIEW,
    PERMISSIONS.MAIN_AREA_UPDATE,
    PERMISSIONS.MAIN_AREA_DELETE,
  ],
  'Budget Management': [
    PERMISSIONS.BUDGET_CREATE,
    PERMISSIONS.BUDGET_VIEW,
    PERMISSIONS.BUDGET_UPDATE,
    PERMISSIONS.BUDGET_DELETE,
    PERMISSIONS.BUDGET_APPROVE,
  ],
  'RAP Management': [
    PERMISSIONS.RAP_CREATE,
    PERMISSIONS.RAP_VIEW,
    PERMISSIONS.RAP_UPDATE,
    PERMISSIONS.RAP_DELETE,
    PERMISSIONS.RAP_APPROVE,
  ],
  'Milestone Management': [
    PERMISSIONS.MILESTONE_CREATE,
    PERMISSIONS.MILESTONE_VIEW,
    PERMISSIONS.MILESTONE_UPDATE,
    PERMISSIONS.MILESTONE_DELETE,
    PERMISSIONS.MILESTONE_APPROVE,
  ],
  'Milestone Template Management': [
    PERMISSIONS.MILESTONE_TEMPLATE_CREATE,
    PERMISSIONS.MILESTONE_TEMPLATE_VIEW,
    PERMISSIONS.MILESTONE_TEMPLATE_UPDATE,
    PERMISSIONS.MILESTONE_TEMPLATE_DELETE,
  ],
  'Supervisor Assignment': [
    PERMISSIONS.SUPERVISOR_ASSIGN,
    PERMISSIONS.SUPERVISOR_VIEW,
    PERMISSIONS.SUPERVISOR_REVOKE,
  ],
  'Evidence Management': [
    PERMISSIONS.EVIDENCE_UPLOAD,
    PERMISSIONS.EVIDENCE_VIEW,
    PERMISSIONS.EVIDENCE_DELETE,
    PERMISSIONS.EVIDENCE_APPROVE,
  ],
  'Report Management': [
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.REPORT_EXPORT,
  ],
  'System Management': [
    PERMISSIONS.SYSTEM_SETTINGS,
    PERMISSIONS.AUDIT_VIEW,
  ],
};

// ============================================
// USER STATUS
// ============================================

export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
};

export const STATUS_LABELS = {
  [USER_STATUS.ACTIVE]: 'Active',
  [USER_STATUS.INACTIVE]: 'Inactive',
  [USER_STATUS.SUSPENDED]: 'Suspended',
};

export const STATUS_COLORS = {
  [USER_STATUS.ACTIVE]: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    badge: 'bg-green-500',
  },
  [USER_STATUS.INACTIVE]: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    badge: 'bg-gray-500',
  },
  [USER_STATUS.SUSPENDED]: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    badge: 'bg-red-500',
  },
};

// ============================================
// ROLE HELPERS
// ============================================

/**
 * Field workers — akses mobile, submit progress & evidence.
 * UI: tombol "Submit Progress" dan "Upload Evidence" tampil.
 * Tombol "Accept/Override/Reject evidence" SEMBUNYI.
 */
export const isFieldRole = (role) => {
  return [
    USER_ROLES.SUPERVISOR,
    USER_ROLES.MANDOR,
  ].includes(role);
};

/**
 * Managers / approvers — akses web, review dan approval.
 *
 * SEBELUM: hanya 4 department managers (gap).
 * SESUDAH: mencakup semua role yang perlu aksi approval di web:
 *   - Project Control: primary approver progress & evidence
 *   - Project Manager: oversee dan approve
 *   - Planning Engineering Manager: milestone & template approval
 *   - Directors: strategic approval
 *   - Other managers: sesuai scope modul
 *
 * UI: tombol "Accept/Override/Reject evidence" TAMPIL.
 *     Tombol "Submit Progress" dan "Upload Evidence" tampil sebagai secondary.
 */
export const isManagerRole = (role) => {
  return [
    // Project operations — primary untuk approval field submission
    USER_ROLES.PROJECT_CONTROL,
    USER_ROLES.PROJECT_MANAGER,
    USER_ROLES.PROJECT_ADMIN,
 
    // Department managers
    USER_ROLES.SALES_MANAGER,
    USER_ROLES.PROCUREMENT_MANAGER,
    USER_ROLES.FINANCE_MANAGER,
    USER_ROLES.PLANNING_ENGINEERING_MANAGER,
 
    // Directors — strategic approval
    USER_ROLES.DIRECTOR,
    USER_ROLES.FINANCE_DIRECTOR,
  ].includes(role);
};

/**
 * Directors — strategic oversight dan approval.
 */
export const isDirectorRole = (role) => {
  return [
    USER_ROLES.DIRECTOR,
    USER_ROLES.FINANCE_DIRECTOR,
  ].includes(role);
};

/**
 * Admin — full system access.
 */
export const isAdminRole = (role) => {
  return role === USER_ROLES.ADMINISTRATOR;
};

/**
 * Can approve field submissions (progress + evidence).
 * Dipakai untuk filter yang lebih spesifik dari isManagerRole.
 */
export const canApproveFieldSubmission = (role) => {
  return [
    USER_ROLES.PROJECT_CONTROL,
    USER_ROLES.PROJECT_MANAGER,
    USER_ROLES.ADMINISTRATOR,
  ].includes(role);
};

// Get role tier
export const getRoleTier = (role) => {
  for (const [tier, roles] of Object.entries(ROLE_TIERS)) {
    if (roles.includes(role)) {
      return parseInt(tier);
    }
  }
  return 4; // Default to operational
};

// ============================================
// API CONFIGURATION
// ============================================

export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api',
  TIMEOUT: 30000,
  ENDPOINTS: {
    AUTH: {
      LOGIN: '/auth/login',
      LOGOUT: '/auth/logout',
      REFRESH: '/auth/refresh',
      ME: '/auth/me',
    },
    USERS: {
      BASE: '/users',
      SEARCH: '/users/search',
      STATISTICS: '/users/statistics',
      BY_ID: (id) => `/users/${id}`,
      ACTIVATE: (id) => `/users/${id}/activate`,
      DEACTIVATE: (id) => `/users/${id}/deactivate`,
      SUSPEND: (id) => `/users/${id}/suspend`,
      UNLOCK: (id) => `/users/${id}/unlock`,
      RESET_PASSWORD: (id) => `/users/${id}/reset-password`,
      CHANGE_PASSWORD: (id) => `/users/${id}/change-password`,
    },
  },
};

// ============================================
// VALIDATION RULES
// ============================================

export const VALIDATION = {
  USERNAME: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 50,
    PATTERN: /^[a-zA-Z0-9._]+$/,
    MESSAGE: 'Username must be 3-50 characters and contain only letters, numbers, dots, and underscores',
  },
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MESSAGE: 'Invalid email format',
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    MESSAGE: 'Password must be at least 8 characters with uppercase, lowercase, and number',
  },
  FULL_NAME: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 100,
    MESSAGE: 'Full name must be 3-100 characters',
  },
  PHONE: {
    PATTERN: /^[0-9+\-\s()]+$/,
    MESSAGE: 'Invalid phone number format',
  },
};

// ============================================
// UI CONSTANTS
// ============================================

export const PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_SIZE: 10,
  SIZE_OPTIONS: [5, 10, 25, 50, 100],
};

export const TOAST_DURATION = {
  SUCCESS: 3000,
  ERROR: 5000,
  INFO: 4000,
  WARNING: 4000,
};

export const COLORS = {
  PRIMARY: {
    blue: 'blue-600',
    red: 'red-600',
  },
  ROLES: {
    [USER_ROLES.ADMINISTRATOR]: 'purple',
    [USER_ROLES.DIRECTOR]: 'indigo',
    [USER_ROLES.FINANCE_DIRECTOR]: 'blue',
    default: 'gray',
  },
};

// ============================================
// SORT DIRECTIONS
// ============================================

export const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
};

// ============================================
// DEFAULT VALUES
// ============================================

export const DEFAULTS = {
  USER_STATUS: USER_STATUS.ACTIVE,
  CAN_ACCESS_WEB: true,
  CAN_ACCESS_MOBILE: false,
  SORT_BY: 'username',
  SORT_DIRECTION: SORT_DIRECTION.ASC,
};